import React, { useMemo, useState } from 'react';
import { useGetAggregatedOrderFulfillmentDataByTypeV2Query } from '../../../graphql/queries/getAggregatedOrderFulfillmentDataByTypeV2.generated';
import { useGetAggregatedEventMerchantOrderDataByTypeQuery } from '../../../graphql/queries/getAggregatedEventMerchantOrderDataByType.generated';
import {
  EventType,
  OrderFulfillmentType,
} from '../../../graphql/generatedTypes';
import i18n from '@/utils/i18n';
import { downloadJsonDataAsExcelFile } from '@/utils/download';
import { FaFileExcel, FaQuestionCircle } from 'react-icons/fa';
import ContainerTypeDisplay from '../../../components/ContainerType/ContainerTypeDisplay';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import CustomMultiSelect, {
  MultiSelectOption,
} from '../../../components/CustomMultiSelect/CustomMultiSelect';
import { ItemTypeCategoryWithContainer } from '@/utils/ItemTypes';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ContainerCountCell } from '@/components/ContainerCountCell';
import { formatContainerCount } from '@/utils/formatContainerCount';

interface EventOrderFulfillmentDataTabProps {
  eventId: string;
  eventType: EventType;
  eventParticipants: any[];
}

interface AggregatedData {
  typeCategory: ItemTypeCategoryWithContainer;
  id: string;
  name: string;
  imageUrl?: string | null;
  colorHexCode?: string | null;
  amountContainersOrdered: number;
  amountContainerStationsOrdered: number;
  picked: number;
  pickedBoxes: number;
  handedOut: number;
  handedOutBoxes: number;
  returnedDirty: number;
  returnedDirtyBoxes: number;
  returnedSealed: number;
  returnedSealedBoxes: number;
  receivedShipmentDirty: number;
  receivedShipmentDirtyBoxes: number;
  receivedShipmentSealed: number;
  receivedShipmentSealedBoxes: number;
  countAndPackCleaning: number;
  countAndPackCleaningBoxes: number;
  packedAfterCleaning: number;
  packedAfterCleaningBoxes: number;
}

// This component is deprecated and will be removed in the future.
const EventOrderFulfillmentDataTab: React.FC<
  EventOrderFulfillmentDataTabProps
> = ({ eventId, eventType, eventParticipants }) => {
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [selectedEventAccountIds, setSelectedEventAccountIds] = useState<
    string[]
  >([]);

  const { data, loading, error } =
    useGetAggregatedOrderFulfillmentDataByTypeV2Query({
      variables: {
        event_id: eventId,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        event_account_ids:
          selectedEventAccountIds.length > 0
            ? selectedEventAccountIds
            : undefined,
      },
    });

  const {
    data: orderData,
    loading: orderLoading,
    error: orderError,
  } = useGetAggregatedEventMerchantOrderDataByTypeQuery({
    variables: {
      event_id: eventId,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      event_account_ids:
        selectedEventAccountIds.length > 0
          ? selectedEventAccountIds
          : undefined,
    },
  });

  const isPerGastronomist = eventType === EventType.StandardPerGastronomist;

  const aggregatedData = useMemo((): AggregatedData[] => {
    // Create a map to store all unique items by ID
    const itemsMap = new Map<string, AggregatedData>();

    // First, add all items from order data
    if (orderData?.getAggregatedEventMerchantOrderDataByType?.container_types) {
      orderData.getAggregatedEventMerchantOrderDataByType.container_types.forEach(
        (ct) => {
          itemsMap.set(ct.item_info.id, {
            typeCategory: 'CONTAINER' as ItemTypeCategoryWithContainer,
            id: ct.item_info.id,
            name: ct.item_info.name,
            imageUrl: ct.item_info.image_url,
            colorHexCode: ct.item_info.color_hex_code,
            amountContainersOrdered: ct.amount_containers_ordered,
            amountContainerStationsOrdered:
              ct.amount_container_stations_ordered,
            picked: 0,
            pickedBoxes: 0,
            handedOut: 0,
            handedOutBoxes: 0,
            returnedDirty: 0,
            returnedDirtyBoxes: 0,
            returnedSealed: 0,
            returnedSealedBoxes: 0,
            receivedShipmentDirty: 0,
            receivedShipmentDirtyBoxes: 0,
            receivedShipmentSealed: 0,
            receivedShipmentSealedBoxes: 0,
            countAndPackCleaning: 0,
            countAndPackCleaningBoxes: 0,
            packedAfterCleaning: 0,
            packedAfterCleaningBoxes: 0,
          });
        },
      );
    }

    // Then, process fulfillment data and merge with existing items
    if (data?.getAggregatedOrderFulfillmentDataByTypeV2) {
      // Process container types
      data.getAggregatedOrderFulfillmentDataByTypeV2.container_types?.forEach(
        (ct) => {
          const existing = itemsMap.get(ct.item_info.id);
          const aggregated: AggregatedData = existing || {
            typeCategory: 'CONTAINER' as ItemTypeCategoryWithContainer,
            id: ct.item_info.id,
            name: ct.item_info.name,
            imageUrl: ct.item_info.image_url,
            colorHexCode: ct.item_info.color_hex_code,
            amountContainersOrdered: 0,
            amountContainerStationsOrdered: 0,
            picked: 0,
            pickedBoxes: 0,
            handedOut: 0,
            handedOutBoxes: 0,
            returnedDirty: 0,
            returnedDirtyBoxes: 0,
            returnedSealed: 0,
            returnedSealedBoxes: 0,
            receivedShipmentDirty: 0,
            receivedShipmentDirtyBoxes: 0,
            receivedShipmentSealed: 0,
            receivedShipmentSealedBoxes: 0,
            countAndPackCleaning: 0,
            countAndPackCleaningBoxes: 0,
            packedAfterCleaning: 0,
            packedAfterCleaningBoxes: 0,
          };

          // Map fulfillment type counts to the aggregated data
          ct.fulfillment_type_counts.forEach((ftCount) => {
            switch (ftCount.fulfillment_type) {
              case OrderFulfillmentType.Picking:
                aggregated.picked = ftCount.amount_sealed;
                aggregated.pickedBoxes = ftCount.amount_container_stations;
                break;
              case OrderFulfillmentType.Delivery:
                aggregated.handedOut = ftCount.amount_sealed;
                aggregated.handedOutBoxes = ftCount.amount_container_stations;
                break;
              case OrderFulfillmentType.Return:
                aggregated.returnedDirty = ftCount.amount;
                aggregated.returnedDirtyBoxes =
                  ftCount.amount_container_stations;
                aggregated.returnedSealed = ftCount.amount_sealed;
                aggregated.returnedSealedBoxes =
                  ftCount.amount_container_stations;
                break;
              case OrderFulfillmentType.ReceiveShipment:
                aggregated.receivedShipmentDirty = ftCount.amount;
                aggregated.receivedShipmentDirtyBoxes =
                  ftCount.amount_container_stations;
                aggregated.receivedShipmentSealed = ftCount.amount_sealed;
                aggregated.receivedShipmentSealedBoxes =
                  ftCount.amount_container_stations_sealed;
                break;
              case OrderFulfillmentType.CountAndPack:
                aggregated.countAndPackCleaning = ftCount.amount;
                aggregated.countAndPackCleaningBoxes =
                  ftCount.amount_container_stations;
                break;
              case OrderFulfillmentType.PackedBoxesAfterCleaning:
                aggregated.packedAfterCleaning = ftCount.amount_sealed;
                aggregated.packedAfterCleaningBoxes =
                  ftCount.amount_container_stations;
                break;
            }
          });

          itemsMap.set(ct.item_info.id, aggregated);
        },
      );

      // Process assets
      data.getAggregatedOrderFulfillmentDataByTypeV2.assets?.forEach((a) => {
        const aggregated: AggregatedData = {
          typeCategory: 'ASSET' as ItemTypeCategoryWithContainer,
          id: a.item_info.id,
          name: a.item_info.name,
          imageUrl: a.item_info.image_url,
          colorHexCode: a.item_info.color_hex_code,
          amountContainersOrdered: 0,
          amountContainerStationsOrdered: 0,
          picked: 0,
          pickedBoxes: 0,
          handedOut: 0,
          handedOutBoxes: 0,
          returnedDirty: 0,
          returnedDirtyBoxes: 0,
          returnedSealed: 0,
          returnedSealedBoxes: 0,
          receivedShipmentDirty: 0,
          receivedShipmentDirtyBoxes: 0,
          receivedShipmentSealed: 0,
          receivedShipmentSealedBoxes: 0,
          countAndPackCleaning: 0,
          countAndPackCleaningBoxes: 0,
          packedAfterCleaning: 0,
          packedAfterCleaningBoxes: 0,
        };

        // Map fulfillment type counts
        a.fulfillment_type_counts.forEach((ftCount) => {
          switch (ftCount.fulfillment_type) {
            case OrderFulfillmentType.Picking:
              aggregated.picked = ftCount.amount_sealed;
              aggregated.pickedBoxes = ftCount.amount_container_stations;
              break;
            case OrderFulfillmentType.Delivery:
              aggregated.handedOut = ftCount.amount_sealed;
              aggregated.handedOutBoxes = ftCount.amount_container_stations;
              break;
            case OrderFulfillmentType.Return:
              aggregated.returnedDirty = ftCount.amount;
              aggregated.returnedDirtyBoxes = ftCount.amount_container_stations;
              aggregated.returnedSealed = ftCount.amount_sealed;
              aggregated.returnedSealedBoxes =
                ftCount.amount_container_stations;
              break;
            case OrderFulfillmentType.ReceiveShipment:
              aggregated.receivedShipmentDirty = ftCount.amount;
              aggregated.receivedShipmentDirtyBoxes =
                ftCount.amount_container_stations;
              aggregated.receivedShipmentSealed = ftCount.amount_sealed;
              aggregated.receivedShipmentSealedBoxes =
                ftCount.amount_container_stations;
              break;
            case OrderFulfillmentType.CountAndPack:
              aggregated.countAndPackCleaning = ftCount.amount;
              aggregated.countAndPackCleaningBoxes =
                ftCount.amount_container_stations;
              break;
            case OrderFulfillmentType.PackedBoxesAfterCleaning:
              aggregated.packedAfterCleaning = ftCount.amount_sealed;
              aggregated.packedAfterCleaningBoxes =
                ftCount.amount_container_stations;
              break;
          }
        });

        itemsMap.set(a.item_info.id, aggregated);
      });

      // Process container stations
      data.getAggregatedOrderFulfillmentDataByTypeV2.container_stations?.forEach(
        (cs) => {
          const aggregated: AggregatedData = {
            typeCategory: 'CONTAINER_STATION' as ItemTypeCategoryWithContainer,
            id: cs.item_info.id,
            name: cs.item_info.name,
            imageUrl: cs.item_info.image_url,
            colorHexCode: cs.item_info.color_hex_code,
            amountContainersOrdered: 0,
            amountContainerStationsOrdered: 0,
            picked: 0,
            pickedBoxes: 0,
            handedOut: 0,
            handedOutBoxes: 0,
            returnedDirty: 0,
            returnedDirtyBoxes: 0,
            returnedSealed: 0,
            returnedSealedBoxes: 0,
            receivedShipmentDirty: 0,
            receivedShipmentDirtyBoxes: 0,
            receivedShipmentSealed: 0,
            receivedShipmentSealedBoxes: 0,
            countAndPackCleaning: 0,
            countAndPackCleaningBoxes: 0,
            packedAfterCleaning: 0,
            packedAfterCleaningBoxes: 0,
          };

          // Map fulfillment type counts
          cs.fulfillment_type_counts.forEach((ftCount) => {
            switch (ftCount.fulfillment_type) {
              case OrderFulfillmentType.Picking:
                aggregated.picked = ftCount.amount_sealed;
                aggregated.pickedBoxes = ftCount.amount_container_stations;
                break;
              case OrderFulfillmentType.Delivery:
                aggregated.handedOut = ftCount.amount_sealed;
                aggregated.handedOutBoxes = ftCount.amount_container_stations;
                break;
              case OrderFulfillmentType.Return:
                aggregated.returnedDirty = ftCount.amount;
                aggregated.returnedDirtyBoxes =
                  ftCount.amount_container_stations;
                aggregated.returnedSealed = ftCount.amount_sealed;
                aggregated.returnedSealedBoxes =
                  ftCount.amount_container_stations;
                break;
              case OrderFulfillmentType.ReceiveShipment:
                aggregated.receivedShipmentDirty = ftCount.amount;
                aggregated.receivedShipmentDirtyBoxes =
                  ftCount.amount_container_stations;
                aggregated.receivedShipmentSealed = ftCount.amount_sealed;
                aggregated.receivedShipmentSealedBoxes =
                  ftCount.amount_container_stations;
                break;
              case OrderFulfillmentType.CountAndPack:
                aggregated.countAndPackCleaning = ftCount.amount;
                aggregated.countAndPackCleaningBoxes =
                  ftCount.amount_container_stations;
                break;
              case OrderFulfillmentType.PackedBoxesAfterCleaning:
                aggregated.packedAfterCleaning = ftCount.amount_sealed;
                aggregated.packedAfterCleaningBoxes =
                  ftCount.amount_container_stations;
                break;
            }
          });

          itemsMap.set(cs.item_info.id, aggregated);
        },
      );
    }

    // Convert map to array, maintaining order: containers, assets, then container stations
    const result: AggregatedData[] = [];
    const containers: AggregatedData[] = [];
    const assets: AggregatedData[] = [];
    const containerStations: AggregatedData[] = [];

    itemsMap.forEach((item) => {
      if (item.typeCategory === 'CONTAINER') {
        containers.push(item);
      } else if (item.typeCategory === 'ASSET') {
        assets.push(item);
      } else if (item.typeCategory === 'CONTAINER_STATION') {
        containerStations.push(item);
      }
    });

    return [...containers, ...assets, ...containerStations];
  }, [data, orderData]);

  const csvData = useMemo(() => {
    if (!aggregatedData.length) return [];
    const rows = aggregatedData.map((item) => {
      if (isPerGastronomist) {
        const diffHandedOutReturned =
          item.handedOut - (item.returnedDirty + item.returnedSealed);
        const diffPickedHandedOut = item.picked - item.handedOut;

        return {
          [i18n.t('Common:item')]: item.name,
          amount_containers_ordered: item.amountContainersOrdered,
          amount_boxes_ordered: item.amountContainerStationsOrdered,
          [i18n.t('Event:pickedClean')]: item.picked,
          [i18n.t('Event:handedOut')]: item.handedOut,
          [i18n.t('Event:returnedDirtyLive')]: item.returnedDirty,
          [i18n.t('Event:returnedSealed')]: item.returnedSealed,
          [i18n.t('Event:receiveShipmentDirty')]: item.receivedShipmentDirty,
          [i18n.t('Event:receiveShipmentSealed')]: item.receivedShipmentSealed,
          [i18n.t('Event:countAndPackCleaning')]: item.countAndPackCleaning,
          [i18n.t('Event:diffHandedOutReturned')]: diffHandedOutReturned,
          [i18n.t('Event:diffPickedHandedOut')]: diffPickedHandedOut,
        };
      } else {
        const isContainerStation = item.typeCategory === 'CONTAINER_STATION';
        const diffPickedReceived = isContainerStation
          ? item.picked -
            item.receivedShipmentDirty -
            item.receivedShipmentSealed
          : item.picked -
            (item.receivedShipmentSealed + item.countAndPackCleaning);

        return {
          [i18n.t('Common:item')]: item.name,
          amount_containers_ordered: item.amountContainersOrdered,
          amount_boxes_ordered: item.amountContainerStationsOrdered,
          [i18n.t('Event:pickedClean')]: item.picked,
          [i18n.t('Event:receiveShipmentDirty')]: item.receivedShipmentDirty,
          [i18n.t('Event:receiveShipmentSealed')]: item.receivedShipmentSealed,
          [i18n.t('Event:countAndPackCleaning')]: item.countAndPackCleaning,
          [i18n.t('Event:lost')]: diffPickedReceived,
        };
      }
    });

    return rows;
  }, [aggregatedData, isPerGastronomist]);

  const handleDownloadExcel = () => {
    const filename = `event-fulfillment-data-${eventId}`;
    downloadJsonDataAsExcelFile(csvData, filename);
  };

  const handleEventAccountChange = (selectedValues: (string | number)[]) => {
    setSelectedEventAccountIds(selectedValues as string[]);
  };

  const eventAccountOptions: MultiSelectOption[] = eventParticipants.map(
    (participant) => ({
      value: participant.id,
      label: participant.name || `Event Account ${participant.id}`,
      disabled: false,
    }),
  );

  if (loading || orderLoading) return <p>{i18n.t('Common:loading')}</p>;
  if (error) return <p>Error loading data: {error.message}</p>;
  if (orderError) return <p>Error loading order data: {orderError.message}</p>;

  return (
    <div className="tw:space-y-4">
      <div className="tw:grid tw:grid-cols-12 tw:gap-4">
        <div className="tw:col-span-12 tw:md:col-span-3">
          <div>
            <label
              htmlFor="startDate"
              className="tw:block tw:mb-2 tw:text-sm tw:font-medium"
            >
              {i18n.t('Common:startDate')}
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-primary"
            />
          </div>
        </div>
        <div className="tw:col-span-12 tw:md:col-span-3">
          <div>
            <label
              htmlFor="endDate"
              className="tw:block tw:mb-2 tw:text-sm tw:font-medium"
            >
              {i18n.t('Common:endDate')}
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-primary"
            />
          </div>
        </div>
        <div className="tw:col-span-12 tw:md:col-span-6">
          <div>
            <label className="tw:block tw:mb-2 tw:text-sm tw:font-medium">
              {i18n.t('Event:eventAccounts')}
            </label>
            <CustomMultiSelect
              options={eventAccountOptions}
              value={selectedEventAccountIds}
              onChange={handleEventAccountChange}
              placeholder={i18n.t('Event:selectEventAccounts')}
              searchable
              clearable
              maxSelectedLabels={3}
              className="tw:*:first:bg-background!"
            />
          </div>
        </div>
      </div>

      <div className="tw:flex tw:justify-between tw:items-center tw:pt-2">
        <h5 className="tw:text-lg tw:font-medium">
          {isPerGastronomist
            ? i18n.t('Event:perGastronomistBilling')
            : i18n.t('Event:fullEventBilling')}
        </h5>
        <Button onClick={handleDownloadExcel} disabled={!csvData.length}>
          <FaFileExcel className="tw:mr-2" />
          {i18n.t('Common:exportExcel')}
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow className="tw:*:first:pl-4 tw:*:last:pr-4 tw:*:last:py-4">
              <TableHead>{i18n.t('Common:item')}</TableHead>
              <TableHead>
                <span className="tw:flex tw:items-center tw:gap-1">
                  {i18n.t('Event:ordered')}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <FaQuestionCircle className="tw:cursor-help tw:text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="tw:max-w-xs">
                        {i18n.t('Event:orderedTooltip')}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </span>
              </TableHead>
              <TableHead>{i18n.t('Event:pickedClean')}</TableHead>
              {isPerGastronomist && (
                <>
                  <TableHead>{i18n.t('Event:handedOut')}</TableHead>
                  <TableHead>{i18n.t('Event:returnedDirtyLive')}</TableHead>
                  <TableHead>{i18n.t('Event:returnedSealed')}</TableHead>
                </>
              )}
              <TableHead>{i18n.t('Event:receiveShipmentDirty')}</TableHead>
              <TableHead>{i18n.t('Event:receiveShipmentSealed')}</TableHead>
              <TableHead>{i18n.t('Event:countAndPackCleaning')}</TableHead>
              {isPerGastronomist ? (
                <>
                  <TableHead>{i18n.t('Event:diffHandedOutReturned')}</TableHead>
                  <TableHead>{i18n.t('Event:diffPickedHandedOut')}</TableHead>
                </>
              ) : (
                <TableHead>
                  <span className="tw:flex tw:items-center tw:gap-1">
                    {i18n.t('Event:lost')}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <FaQuestionCircle className="tw:cursor-help tw:text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="tw:max-w-xs">
                          {i18n.t('Event:diffPickedReceived')}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </span>
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {aggregatedData.map((item) => {
              const isContainerStation =
                item.typeCategory === 'CONTAINER_STATION';
              const diffHandedOutReturned = isPerGastronomist
                ? item.handedOut - (item.returnedDirty + item.returnedSealed)
                : 0;
              const diffPickedHandedOut = isPerGastronomist
                ? item.picked - item.handedOut
                : 0;
              const diffPickedReceived = !isPerGastronomist
                ? isContainerStation
                  ? item.picked -
                    item.receivedShipmentDirty -
                    item.receivedShipmentSealed
                  : item.picked -
                    (item.receivedShipmentSealed + item.countAndPackCleaning)
                : 0;

              const formatCell = (containers: number, boxes: number) =>
                isContainerStation
                  ? containers
                  : formatContainerCount(containers, boxes);

              return (
                <TableRow
                  key={item.id}
                  className="tw:*:first:pl-4 tw:*:last:pr-4"
                >
                  <TableCell>
                    <div className="tw:[&_img]:bg-white">
                      <ContainerTypeDisplay
                        showContainerId={false}
                        containerType={{
                          id: 1,
                          name: item.name,
                          imageUrl: item.imageUrl || null,
                          hexCode: item.colorHexCode || null,
                        }}
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <ContainerCountCell
                      amountOfContainers={item.amountContainersOrdered}
                      amountOfBoxes={item.amountContainerStationsOrdered}
                    />
                  </TableCell>
                  <TableCell>
                    {formatCell(item.picked, item.pickedBoxes)}
                  </TableCell>
                  {isPerGastronomist && (
                    <>
                      <TableCell>
                        {formatCell(item.handedOut, item.handedOutBoxes)}
                      </TableCell>
                      <TableCell>
                        {formatCell(
                          item.returnedDirty,
                          item.returnedDirtyBoxes,
                        )}
                      </TableCell>
                      <TableCell>
                        {formatCell(
                          item.returnedSealed,
                          item.returnedSealedBoxes,
                        )}
                      </TableCell>
                    </>
                  )}
                  <TableCell>
                    {isContainerStation ? (
                      item.receivedShipmentDirty
                    ) : (
                      <>
                        {item.receivedShipmentDirtyBoxes}{' '}
                        {item.receivedShipmentDirtyBoxes === 1
                          ? i18n.t('Event:box')
                          : i18n.t('Event:boxes')}
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {isContainerStation ? (
                      item.receivedShipmentSealed
                    ) : (
                      <>
                        {item.receivedShipmentSealedBoxes}{' '}
                        {item.receivedShipmentSealedBoxes === 1
                          ? i18n.t('Event:box')
                          : i18n.t('Event:boxes')}
                        <span className="tw:pl-1 tw:text-neutral-500 tw:dark:text-neutral-400">
                          ({item.receivedShipmentSealed.toLocaleString()}{' '}
                          {item.receivedShipmentSealed === 1
                            ? i18n.t('Event:container')
                            : i18n.t('Event:containers')}
                          )
                        </span>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {isContainerStation ? (
                      '-'
                    ) : (
                      <span className="tw:flex tw:items-center tw:gap-1">
                        {formatCell(
                          item.countAndPackCleaning,
                          item.packedAfterCleaningBoxes,
                        )}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <FaQuestionCircle className="tw:cursor-help tw:text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="tw:max-w-xs">
                              {i18n.t('Event:countAndPackCleaningTooltip', {
                                count:
                                  item.countAndPackCleaning -
                                  item.packedAfterCleaning,
                              })}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </span>
                    )}
                  </TableCell>
                  {isPerGastronomist ? (
                    <>
                      <TableCell>{diffHandedOutReturned}</TableCell>
                      <TableCell>{diffPickedHandedOut}</TableCell>
                    </>
                  ) : (
                    <TableCell>{diffPickedReceived}</TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {aggregatedData.length === 0 && (
        <p className="tw:text-gray-500 tw:text-center">
          {i18n.t('Event:noFulfillmentData')}
        </p>
      )}
    </div>
  );
};

export default EventOrderFulfillmentDataTab;
